import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import InputAdornment from "@mui/material/InputAdornment"
import Skeleton from "@mui/material/Skeleton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { endOfDay, startOfDay, subDays } from "date-fns"
import { useEffect, useState } from "react"
import { useDebounceValue } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column, {
  CenterEverythingColumn,
} from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import { formatDateTime, formatPhone } from "../../../common/utils"
import { getCallDispositionLabel } from "../calling/callDispositions"
import {
  sessionOutcomeRows,
  sessionSourceLabel,
  sessionSummaryCounts,
} from "../calling/powerDialer"
import DialogHeader from "../components/DialogHeader"

const STATUS_CHIP = {
  active: { label: "In progress", color: "primary" },
  paused: { label: "Paused", color: "warning" },
  ended: { label: "Ended", color: "default" },
}

function queueItemStatusLabel(item) {
  if (item?.is_dnc || item?.skip_reason === "dnc") return "Skipped — DNC"
  if (item?.status === "completed") {
    return getCallDispositionLabel(item.disposition) || "Logged"
  }
  if (item?.status === "skipped") return "Skipped"
  return "Not called"
}

export default function PowerDialingHistoryTab() {
  const axios = useAuthorizedAxiosClient()
  const [sessions, setSessions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [dateFrom, setDateFrom] = useState(() =>
    startOfDay(subDays(new Date(), 7))
  )
  const [dateTo, setDateTo] = useState(() => endOfDay(new Date()))
  const [selectedSession, setSelectedSession] = useState(null)
  const [sessionDetail, setSessionDetail] = useState(null)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [pageIndex, setPageIndex] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalResultsSize, setTotalResultsSize] = useState(0)
  const [searchText, setSearchText] = useState("")
  const [debouncedSearchText] = useDebounceValue(searchText, 400)

  useEffect(() => {
    setIsLoading(true)
    const params = {
      page: pageIndex,
      page_size: pageSize,
    }
    if (debouncedSearchText) {
      params.q = debouncedSearchText
    }
    if (dateFrom) {
      params.date_from = dateFrom.toISOString()
    }
    if (dateTo) {
      params.date_to = dateTo.toISOString()
    }
    axios
      .get("/crm/api/dialer/sessions/", { params })
      .then(({ data }) => {
        setSessions(data.results ?? [])
        setTotalResultsSize(data.count ?? 0)
      })
      .catch(() => {
        setSessions([])
        setTotalResultsSize(0)
      })
      .finally(() => setIsLoading(false))
  }, [axios, dateFrom, dateTo, debouncedSearchText, pageIndex, pageSize])

  useEffect(() => {
    if (!selectedSession?.id) {
      setSessionDetail(null)
      return undefined
    }
    let cancelled = false
    setIsDetailLoading(true)
    axios
      .get(`/crm/api/dialer/sessions/${selectedSession.id}/`)
      .then(({ data }) => {
        if (!cancelled) setSessionDetail(data)
      })
      .catch(() => {
        if (!cancelled) setSessionDetail(selectedSession)
      })
      .finally(() => {
        if (!cancelled) setIsDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [axios, selectedSession])

  function handleDateFromChanged(date) {
    if (!date) return
    setDateFrom(startOfDay(date))
    setPageIndex(1)
  }

  function handleDateToChanged(date) {
    if (!date) return
    setDateTo(endOfDay(date))
    setPageIndex(1)
  }

  return (
    <Column gap={2}>
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <Typography variant="body2" color="text.secondary">
        Named Power Dialer sessions for this account. Open a session to see
        every number that was called.
      </Typography>
      <RowButColumnOnMobile sx={{ gap: 2, alignItems: "center" }}>
        <TextField
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value)
            setPageIndex(1)
          }}
          placeholder="Search by session name"
          size="small"
          sx={{
            minWidth: 280,
            flex: 1,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "colors.white",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ color: "colors.gray700" }} />
              </InputAdornment>
            ),
          }}
        />
        <DatePicker
          label="From"
          value={dateFrom}
          onChange={handleDateFromChanged}
          maxDate={dateTo}
          slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
        />
        <DatePicker
          label="To"
          value={dateTo}
          onChange={handleDateToChanged}
          minDate={dateFrom}
          maxDate={new Date()}
          slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
        />
      </RowButColumnOnMobile>
      {isLoading ? (
        <Column gap={1.5}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={120} />
        </Column>
      ) : sessions.length === 0 ? (
        <CenterEverythingColumn sx={{ py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            No Power Dialer sessions in this date range.
          </Typography>
        </CenterEverythingColumn>
      ) : (
        <Column gap={1.5}>
          {sessions.map((session) => (
            <SessionHistoryCard
              key={session.id}
              session={session}
              onOpen={() => setSelectedSession(session)}
            />
          ))}
        </Column>
      )}
      <TablePaginationRow
        tableRows={sessions}
        pageIndex={pageIndex}
        setPageIndex={(nextPage) => setPageIndex(nextPage)}
        pageSize={pageSize}
        setPageSize={(nextSize) => {
          setPageSize(nextSize)
          setPageIndex(1)
        }}
        totalResultsSize={totalResultsSize}
      />
      <SessionCalledNumbersDialog
        open={Boolean(selectedSession)}
        session={sessionDetail || selectedSession}
        isLoading={isDetailLoading}
        onClose={() => {
          setSelectedSession(null)
          setSessionDetail(null)
        }}
      />
    </Column>
  )
}

function SessionHistoryCard({ session, onOpen }) {
  const { logged, skipped, booked, attempted } = sessionSummaryCounts(session)
  const remaining = Number(session.remaining_count) || 0
  const outcomes = sessionOutcomeRows(session)
  const status = STATUS_CHIP[session.status] || STATUS_CHIP.ended
  const startedBy = session.started_by?.name

  return (
    <Column
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        p: 2,
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "colors.gray200",
        backgroundColor: "colors.white",
        boxShadow: "0 10px 24px -16px rgba(15, 23, 42, 0.28)",
        "&:hover": {
          borderColor: "colors.brand200",
        },
      }}
    >
      <Row
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Column gap={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {session.name || "Untitled session"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sessionSourceLabel(session.source)}
            {session.created ? ` · ${formatDateTime(session.created)}` : ""}
            {startedBy ? ` · By ${startedBy}` : ""}
          </Typography>
        </Column>
        <Chip size="small" label={status.label} color={status.color} />
      </Row>
      <Row sx={{ gap: 1, flexWrap: "wrap" }}>
        <SummaryStat label="Logged" value={logged} />
        <SummaryStat label="Skipped" value={skipped} />
        <SummaryStat label="Booked" value={booked} />
        <SummaryStat label="Attempted" value={attempted} emphasis />
      </Row>
      {remaining > 0 ? (
        <Typography variant="body2" color="text.secondary">
          {remaining} lead{remaining === 1 ? " was" : "s were"} not called.
        </Typography>
      ) : null}
      {outcomes.length ? (
        <Row sx={{ gap: 1, flexWrap: "wrap" }}>
          {outcomes.map((row) => (
            <Chip
              key={row.value}
              size="small"
              variant="outlined"
              label={`${row.label} ${row.count}`}
            />
          ))}
        </Row>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No call outcomes were logged.
        </Typography>
      )}
    </Column>
  )
}

function SummaryStat({ label, value, emphasis = false }) {
  return (
    <Column
      sx={{
        flex: "1 1 72px",
        minWidth: 72,
        p: 1.25,
        borderRadius: 1.5,
        alignItems: "center",
        bgcolor: emphasis ? "colors.brand50" : "colors.gray50",
        border: "1px solid",
        borderColor: emphasis ? "colors.brand200" : "colors.gray200",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700 }}
      >
        {label}
      </Typography>
    </Column>
  )
}

function SessionCalledNumbersDialog({ open, session, isLoading, onClose }) {
  const items = Array.isArray(session?.queue_items) ? session.queue_items : []
  const { logged, skipped, booked, attempted } = sessionSummaryCounts(session)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title={session?.name || "Power Dialer session"}
        onClose={onClose}
      />
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <DialogContent>
        <Column gap={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {sessionSourceLabel(session?.source)}
            {session?.created ? ` · ${formatDateTime(session.created)}` : ""}
          </Typography>
          <Row sx={{ gap: 1, flexWrap: "wrap" }}>
            <SummaryStat label="Logged" value={logged} />
            <SummaryStat label="Skipped" value={skipped} />
            <SummaryStat label="Booked" value={booked} />
            <SummaryStat label="Attempted" value={attempted} emphasis />
          </Row>
          {isLoading && !items.length ? (
            <Skeleton variant="rounded" height={80} />
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No numbers were queued in this session.
            </Typography>
          ) : (
            <Column gap={1}>
              {items.map((item, index) => (
                <Row
                  key={`${item.lead_ref_id || index}-${index}`}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 1,
                    borderRadius: 1.5,
                    bgcolor: "colors.gray50",
                  }}
                >
                  <Column sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {item.name || "Unknown"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.phone ? formatPhone(item.phone) : "No number"}
                    </Typography>
                  </Column>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    {queueItemStatusLabel(item)}
                  </Typography>
                </Row>
              ))}
            </Column>
          )}
        </Column>
      </DialogContent>
    </Dialog>
  )
}
